import { isBefore, parseISO } from 'date-fns';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Portal, rem, ScaleFade, Text, useUserColorMode } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { UserContentDismissal, UserContentDismissalRef } from '../../../components/profile/user-content-dismissal';

const DARK_MODE_ALERT_NAMESPACE = 'dark_mode';
const DARK_MODE_ALERT_NAME = 'dark-mode-release-announcement';

// Only show the dark mode announcement to users who existed before the feature was released
const DARK_MODE_RELEASE_DATE = new Date('2026-01-16');

interface DarkModeAlertProps {
	isExpanded: boolean;
}

export interface DarkModeAlertRef {
	dismiss: () => void;
}

export const DarkModeAlert = forwardRef<DarkModeAlertRef, DarkModeAlertProps>(({ isExpanded }, ref) => {
	const { t } = useTranslation('navigation');
	const [showAlert, setShowAlert] = useState(false);
	const { canChangeColorMode } = useUserColorMode();
	const user = useCurrentUser();
	const anchorRef = useRef<HTMLDivElement>(null);
	const dismissalRef = useRef<UserContentDismissalRef>(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });

	// Only show to users who existed before dark mode was released
	const userPredatesDarkModeRelease = isBefore(parseISO(user.created_at), DARK_MODE_RELEASE_DATE);

	// Determine if the visual alert should be shown
	const shouldShowVisualAlert = isExpanded && canChangeColorMode && userPredatesDarkModeRelease;

	useImperativeHandle(ref, () => ({
		dismiss: () => {
			dismissalRef.current?.dismiss();
		},
	}));

	useEffect(() => {
		const timeout = setTimeout(() => {
			setShowAlert(true);
		}, 1000);

		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		const updatePosition = () => {
			if (anchorRef.current) {
				const rect = anchorRef.current.getBoundingClientRect();
				setPosition({
					top: rect.top - 8, // Position above the anchor with margin
					left: rect.left + rect.width / 2, // Center on the dots icon area
				});
			}
		};

		// Delay position update to allow sidebar transition to complete
		const timeout = setTimeout(updatePosition, 300);
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition);

		return () => {
			clearTimeout(timeout);
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition);
		};
	}, [showAlert, isExpanded]);

	// Don't render anything if user can't change color mode or is a new user
	if (!canChangeColorMode || !userPredatesDarkModeRelease) {
		return null;
	}

	// Always render UserContentDismissal to maintain the ref for imperative dismiss calls,
	// but only render the visual alert when the sidebar is expanded
	return (
		<UserContentDismissal ref={dismissalRef} name={DARK_MODE_ALERT_NAME} namespace={DARK_MODE_ALERT_NAMESPACE}>
			{(updateUserContentDismissal) =>
				shouldShowVisualAlert ? (
					<>
						{/* Invisible anchor to track position */}
						<Box ref={anchorRef} position="absolute" top={0} right={rem(24)} w={0} h={0} />

						<Portal>
							<ScaleFade initialScale={0.9} in={showAlert}>
								<Box position="fixed" top={`${position.top}px`} left={`${position.left}px`} transform="translate(-30%, -100%)" w={rem(280)} zIndex="popover">
									<Box
										bg="primary.bg"
										color="text.onColor"
										p={rem(16)}
										borderRadius="lg"
										boxShadow="lg"
										position="relative"
										_after={{
											content: '""',
											position: 'absolute',
											bottom: rem(-8),
											left: '30%',
											width: 0,
											height: 0,
											borderLeft: '8px solid transparent',
											borderRight: '8px solid transparent',
											borderTop: '8px solid',
											borderTopColor: 'primary.bg',
										}}
									>
										<Text fontWeight="bold" fontSize={100} mb={1}>
											{t('dark_mode_alert.title')}
										</Text>
										<Text fontSize={50} opacity={0.85} mb={3}>
											{t('dark_mode_alert.body')}
										</Text>
										<Box display="flex" justifyContent="flex-end">
											<Button
												size="sm"
												variant="outline"
												color="text.onColor"
												borderColor="text.onColor"
												_hover={{ bg: 'primary.fgStronger' }}
												onClick={() => updateUserContentDismissal()}
											>
												{t('dark_mode_alert.dismiss')}
											</Button>
										</Box>
									</Box>
								</Box>
							</ScaleFade>
						</Portal>
					</>
				) : null
			}
		</UserContentDismissal>
	);
});

DarkModeAlert.displayName = 'DarkModeAlert';
